import axios from 'axios';
import { API_URLS, WWW_URLS } from '../urls';
import { BackendResponse, handleError } from '../../dataTypes/backendServicesManagment/types';
import { TranscodingState } from '../../dataTypes/mediaManagment/types';

export interface BackendResponse_TranscodingVideo extends BackendResponse<TranscodingState> {}

export const getVideoTranscodingProgress = async (mediaId: number): Promise<BackendResponse_TranscodingVideo> => {
  const baseUrl = API_URLS.getVideoTranscodingProgress;
  const url = baseUrl.replace('{mediaId}', `${mediaId}`);

  try {
    const response = await axios.get(url);
    console.log(url, response);

    if (response.status === 200) {

      const transcodingState : TranscodingState = {

        mediaId           : response.data.mediaId ,
        progress          : response.data.progress ,
        timecodeInSeconds : response.data.timecodeInSeconds ,
        totalDuration     : response.data.totalDuration ,
        transcoded        : response.data.transcoded === 'true'
      }

      return {
        code: 1,
        comment: "The retrieval of transcoding progress is completed.",
        payload: transcodingState,
      };
    } else {
      return handleError<TranscodingState>(response.status, "Non-200 response from API");
    }
  } catch (error) {
    console.log(url, error);

    if (axios.isAxiosError(error)) {
      return handleError<TranscodingState>(-101, error.response?.data?.detail ?? error.message);
    }

    return handleError<TranscodingState>(-102, "Unknown error");
  }
};

/**
 * Accès direct au microservice de transcodage (progression live), en contournant Symfony.
 * Routé via HAProxy (https://cast.kleonpro.tv/{uuid}/transcoding/progress) vers le container
 * Docker éphémère du worker (cf. admin-api/src/Service/DockerTranscodingHelpers.php,
 * video-transcoding-worker/app/progress_server.py). Le webhook reste la source de vérité pour
 * l'écriture finale en base ; cet accès direct ne sert qu'à afficher la progression sans
 * repasser par Symfony à chaque poll.
 *
 * Échoue (404/503/timeout) tant que le container n'est pas encore enregistré auprès de
 * HAProxy, ou une fois le transcodage terminé et la route nettoyée : l'appelant doit alors
 * retomber sur getVideoTranscodingProgress() (Symfony).
 */
export const getVideoTranscodingProgressDirect = async (uuid: string): Promise<BackendResponse_TranscodingVideo> => {
  const url = `${WWW_URLS.broadcastingServer}/${uuid}/transcoding/progress`;

  try {
    const response = await axios.get(url, { timeout: 2000 });

    if (response.status === 200) {
      const transcodingState: TranscodingState = {
        mediaId           : 0,
        progress          : response.data.progress_percent ?? 0,
        timecodeInSeconds : response.data.current_time ?? 0,
        totalDuration     : 0,
        transcoded        : response.data.status === 'completed',
      };

      return {
        code: 1,
        comment: "The retrieval of transcoding progress (direct) is complete.",
        payload: transcodingState,
      };
    }

    return handleError<TranscodingState>(response.status, "Non-200 response from direct microservice access");
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<TranscodingState>(-101, error.response?.data?.detail ?? error.message);
    }

    return handleError<TranscodingState>(-102, "Unknown error");
  }
};

export interface BackendResponse_TranscodingSalesCampaign extends BackendResponse<number> {}

export const getSalesCampaignTranscodingProgress = async (salesCampaignId: number): Promise<BackendResponse_TranscodingSalesCampaign> => {
  const baseUrl = API_URLS.getSalesCampaignTranscodingProgress;
  const url = baseUrl.replace('{salesCampaignId}', `${salesCampaignId}`);

  try {
    const response = await axios.get(url);
    console.log(url, response);

    if (response.status === 200) {
      const transcodingProgress = response.data.globaleProgress;

      return {
        code: 1,
        comment: "The retrieval of transcoding progress is completed.",
        payload: transcodingProgress,
      };
    } else {
      return handleError<number>(response.status, "Non-200 response from API");
    }
  } catch (error) {
    console.log(url, error);

    if (axios.isAxiosError(error)) {
      return handleError<number>(-101, error.response?.data?.detail ?? error.message);
    }

    return handleError<number>(-102, "Unknown error");
  }
};
